<template>
  <div class="container-fluid p-4">
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="row">
            <h3>{{ $t('import.ledghome.title') }}</h3>
            <p>{{ $t('import.ledghome.description') }}</p>
          </div>

          <!-- File Drop Area -->
          <div class="row mt-4">
            <div class="col-12">
              <div
                class="file-drop-area"
                :class="{ 'drag-over': isDragging }"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                @click="triggerFileInput"
              >
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  accept=".csv,.txt"
                  @change="handleFileSelect"
                  style="display: none"
                />
                <div v-if="selectedFiles.length === 0" class="drop-message">
                  <div>
                    {{ $t('import.ledghome.dropMessage') }}
                  </div>
                </div>
                <div v-else class="file-list">
                  <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
                    {{ file.name }}
                    <button
                      type="button"
                      class="btn btn-sm btn-link text-danger"
                      @click.stop="removeFile(index)"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Import Button -->
          <div class="row mt-4">
            <div class="col-12 text-center">
              <button
                type="button"
                class="btn btn-primary btn-lg"
                @click="handleImport"
                :disabled="selectedFiles.length === 0 || isImporting"
              >
                <span v-if="isImporting">
                  <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {{ $t('import.ledghome.importing') }}
                </span>
                <span v-else>
                  {{ $t('import.ledghome.importButton') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Import Result Dialog -->
    <ImportResultDialog
      v-model="showResultDialog"
      :results="importResults"
    />

    <!-- Error Dialog -->
    <ErrorDialog
      v-model="showErrorDialog"
      :message="errorDialogMessage"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import ImportResultDialog from '@/components/user/import/ImportResultDialog.vue';
import ErrorDialog from '@/components/common/ErrorDialog.vue';
import { colors } from '../../../../consts/colors';

const { t } = useI18n();

const isDragging = ref(false);
const selectedFiles = ref([]);
const fileInput = ref(null);
const isImporting = ref(false);
const showResultDialog = ref(false);
const showErrorDialog = ref(false);
const errorDialogMessage = ref('');
const importResults = ref({
  total_files: 0,
  processed_files: 0,
  total_rows: 0,
  created_donators: 0,
  updated_donators: 0,
  created_stockholders: 0,
  updated_stockholders: 0,
  deleted_stockholders: 0,
  errors: {}
});

/**
 * Handle drag over event
 */
const handleDragOver = () => {
  isDragging.value = true;
};

/**
 * Handle drag leave event
 */
const handleDragLeave = () => {
  isDragging.value = false;
};

/**
 * Handle file drop
 */
const handleDrop = (event) => {
  isDragging.value = false;
  const files = Array.from(event.dataTransfer.files);
  addFiles(files);
};

/**
 * Trigger file input click
 */
const triggerFileInput = () => {
  fileInput.value.click();
};

/**
 * Handle file selection from input
 */
const handleFileSelect = (event) => {
  const files = Array.from(event.target.files);
  addFiles(files);
  // Reset file input
  event.target.value = '';
};

/**
 * Add files to selected files list
 */
const addFiles = (files) => {
  // Filter CSV files only
  const csvFiles = files.filter(file => {
    return file.name.endsWith('.csv') || file.name.endsWith('.txt');
  });

  // Check if adding these files would exceed the limit
  if (selectedFiles.value.length + csvFiles.length > 10) {
    errorDialogMessage.value = t('import.ledghome.maxFilesError', { max: 10 });
    showErrorDialog.value = true;
    return;
  }

  // Add files
  selectedFiles.value.push(...csvFiles);
};

/**
 * Remove a file from selected files
 */
const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
};

/**
 * Handle import
 */
const handleImport = async () => {
  if (selectedFiles.value.length === 0) {
    return;
  }

  isImporting.value = true;

  try {
    const formData = new FormData();
    selectedFiles.value.forEach((file) => {
      formData.append('files[]', file);
    });

    const response = await axios.post('/api/user/import/ledghome', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Set import results and show dialog
    importResults.value = response.data.data;
    showResultDialog.value = true;

    // Clear selected files after successful import
    selectedFiles.value = [];

  } catch (error) {
    console.error('Import error:', error);

    // Check if this is a 422 validation error with data
    if (error.response?.status === 422 && error.response?.data?.data) {
      // Set import results and show dialog
      importResults.value = error.response.data.data;
      showResultDialog.value = true;
    } else if (error.response?.data?.message) {
      // Show error in dialog for non-import errors
      errorDialogMessage.value = error.response.data.message;
      showErrorDialog.value = true;
    } else if (error.response?.data?.errors) {
      // Handle validation errors
      const errors = error.response.data.errors;
      errorDialogMessage.value = Object.values(errors).flat().join(', ');
      showErrorDialog.value = true;
    } else {
      errorDialogMessage.value = t('import.ledghome.importError');
      showErrorDialog.value = true;
    }
  } finally {
    isImporting.value = false;
  }
};
</script>

<style lang="scss" scoped>
.file-drop-area {
  min-height: 300px;
  border: 2px dashed v-bind('colors.border');
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: v-bind('colors.white');

  &:hover {
    border-color: v-bind('colors.blue[500]');
    background-color: v-bind('colors.bgHover');
  }

  &.drag-over {
    border-color: v-bind('colors.blue[500]');
    background-color: v-bind('colors.chartBlue[150]');
  }

  .drop-message {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 260px;
    color: v-bind('colors.textSecondary');
    font-size: 1.1rem;
  }

  .file-list {
    text-align: left;
    max-height: 300px;
    overflow-y: auto;

    .file-item {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid v-bind('colors.borderLight');
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color: v-bind('colors.white');

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background-color: v-bind('colors.bgHover');
      }

      .btn-link {
        padding: 0;
        text-decoration: none;
        font-weight: bold;
        font-size: 1.2rem;
      }
    }
  }
}

.btn-primary {
  background-color: v-bind('colors.blue[500]');
  border-color: v-bind('colors.blue[500]');
  min-width: 200px;
  padding: 0.75rem 2rem;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
}
</style>
